import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, ThemeProvider, PermissionProvider, useAuth, usePermissions } from "@/lib/api";

// Hidden route guard — checks sessionStorage for time-limited access token
function HiddenRoute({ children }) {
    const ts = sessionStorage.getItem('_x');
    if (!ts || Date.now() - parseInt(ts) > 5 * 60 * 1000) {
        sessionStorage.removeItem('_x');
        return <Navigate to="/home" replace />;
    }
    return children;
}

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import BirdsEyeDashboard from "@/pages/BirdsEyeDashboard";
import SalesCRMPage from "@/pages/SalesCRMPage";
import SalesDashboard from "@/pages/SalesDashboard";
import ProductivityDashboardPage from "@/pages/ProductivityDashboardPage";
import SalesDirectoryPage from "@/pages/SalesDirectoryPage";
import SecurityIntelligencePage from "@/pages/SecurityIntelligencePage";
import CompetitorIntelligencePage from "@/pages/CompetitorIntelligencePage";
import HistoricalImportPage from "@/pages/sales/HistoricalImportPage";
import DormantLeadsPage from "@/pages/sales/DormantLeadsPage";
import ReturningLeadsPage from "@/pages/sales/ReturningLeadsPage";
import LeadAssignmentAuditPage from "@/pages/sales/LeadAssignmentAuditPage";
import LeadAnalysisPage from "@/pages/analytics/LeadAnalysisPage";
import CSHistoricalImportPage from "@/pages/cs/CSHistoricalImportPage";
import CustomerServicePage from "@/pages/CustomerServicePage";
import MentorCRMPage from "@/pages/MentorCRMPage";
import UsersPage from "@/pages/UsersPage";
import DepartmentsPage from "@/pages/DepartmentsPage";
import CoursesPage from "@/pages/CoursesPage";
import CommissionEnginePage from "@/pages/CommissionEnginePage";
import AccessControlPage from "@/pages/AccessControlPage";
import CSDashboard from "@/pages/CSDashboard";
import StudentDirectoryPage from "@/pages/StudentDirectoryPage";
import MergeApprovalsPage from "@/pages/MergeApprovalsPage";
import LeadsPoolPage from "@/pages/LeadsPoolPage";
import CustomerMasterPage from "@/pages/CustomerMasterPage";
import FollowupsPage from "@/pages/FollowupsPage";
import MentorDashboardPage from "@/pages/MentorDashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminExportsPage from "@/pages/AdminExportsPage";
import DeveloperGuidePage from "@/pages/DeveloperGuidePage";
import WelcomePage from "@/pages/WelcomePage";
import QCDashboardPage from "@/pages/QCDashboardPage";
import AuditLogPage from "@/pages/AuditLogPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import RolesPage from "@/pages/RolesPage";
import TeamsPage from "@/pages/TeamsPage";
import MentorLeaderboard from "@/pages/MentorLeaderboard";
import MentorHistoricalImportPage from "@/pages/academics/MentorHistoricalImportPage";
import BDCRMPage from "@/pages/BDCRMPage";
import BDDashboardPage from "@/pages/BDDashboardPage";
import BDCalculatorPage from "@/pages/BDCalculatorPage";
import SharedCallLogPage from "@/pages/SharedCallLogPage";
import MetaHubPage from "@/pages/MetaHubPage";
import CertificatePage from "@/pages/CertificatePage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import ProductivityReportPage from "@/pages/ProductivityReportPage";
import ReportSchedulerPage from "@/pages/ReportSchedulerPage";
import RevenueForecastPage from "@/pages/RevenueForecastPage";
import StudentPortalPage from "@/pages/StudentPortalPage";
import CommissionDashboard from "@/pages/CommissionDashboard";
import CrossMentorDepositsPage from "@/pages/CrossMentorDepositsPage";
import DuplicateManagerPage from "@/pages/DuplicateManagerPage";
import MT5SyncPage from "@/pages/MT5SyncPage";
import ChatPage from "@/pages/ChatPage";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import OrganizationMapPage from "@/pages/OrganizationMapPage";
import ITAssetsPage from "@/pages/ITAssetsPage";
import AssetManagementPage from "@/pages/it/AssetManagementPage";
import AssetScanPage from "@/pages/it/AssetScanPage";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import SaaSSubscriptionsPage from "@/pages/it/SaaSSubscriptionsPage";
import MarketingExtrasPage from "@/pages/marketing/MarketingExtrasPage";
import OffboardingPage from "@/pages/hr/OffboardingPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import ClaretDashboardPage from "@/pages/ClaretDashboardPage";
import ClaretAdminPage from "@/pages/ClaretAdminPage";
import ClaretConfigPage from "@/pages/ClaretConfigPage";
import ObjectionLibraryPage from "@/pages/ObjectionLibraryPage";
import ChatMonitorPage from "@/pages/ChatMonitorPage";
import CallIntelligencePage from "@/pages/CallIntelligencePage";
import CallAnalyticsPage from "@/pages/CallAnalyticsPage";
import ScriptLearningPage from "@/pages/ScriptLearningPage";
import RealtimeAssistPage from "@/pages/RealtimeAssistPage";
import TaskManagerPage from "@/pages/TaskManagerPage";
import BulkAttendancePage from "@/pages/BulkAttendancePage";

// Finance Module
import FinanceEntitySelector from "@/pages/FinanceEntitySelector";
import FinanceLayout from "@/pages/FinanceLayout";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import JournalPage from "@/pages/finance/JournalPage";
import SettlementsPage from "@/pages/finance/SettlementsPage";
import ExpensesPage from "@/pages/finance/ExpensesPage";
import TransfersPage from "@/pages/finance/TransfersPage";
import AccountsPage from "@/pages/finance/AccountsPage";
import FinanceCommissionEnginePage from "@/pages/finance/CommissionEnginePage";
import CommissionSettlementsPage from "@/pages/finance/CommissionSettlementsPage";
import ReconciliationPage from "@/pages/finance/ReconciliationPage";
import FinanceVerificationsPage from "@/pages/finance/FinanceVerificationsPage";
import PendingSettlementsPage from "@/pages/finance/PendingSettlementsPage";
// Finance Suite - CLT
import CltFinanceDashboard from "@/pages/finance/CltFinanceDashboard";
import CltPayablesPage from "@/pages/finance/CltPayablesPage";
import CltReceivablesPage from "@/pages/finance/CltReceivablesPage";
// Finance Suite - Treasury
import TreasuryDashboard from "@/pages/finance/TreasuryDashboard";
import TreasuryBalancesPage from "@/pages/finance/TreasuryBalancesPage";
import TreasurySettlementsPage from "@/pages/finance/TreasurySettlementsPage";
// Finance Suite - Budgeting
import BudgetSheetPage from "@/pages/finance/BudgetSheetPage";
import BudgetDashboard from "@/pages/finance/BudgetDashboard";
// Finance Suite - PNL & Data
import OverallPNLDashboard from "@/pages/finance/OverallPNLDashboard";
import DataManagementPage from "@/pages/finance/DataManagementPage";
// Finance Suite - Settings & Vendors
import FinanceSettingsPage from "@/pages/finance/FinanceSettingsPage";
import VendorManagementPage from "@/pages/finance/VendorManagementPage";
import WithdrawalsPage from "@/pages/finance/WithdrawalsPage";
import UnifiedTransactionsPage from "@/pages/finance/UnifiedTransactionsPage";
// Finance Dashboard Router
import FinanceDashboardRouter from "@/pages/FinanceDashboardRouter";

// HR Module
import HRDashboard from "@/pages/hr/HRDashboard";
import EmployeeMasterPage from "@/pages/hr/EmployeeMasterPage";
import EmployeeDetailsPage from "@/pages/hr/EmployeeDetailsPage";
import LeavePage from "@/pages/hr/LeavePage";
import AttendancePage from "@/pages/hr/AttendancePage";
import AttendanceSettingsPage from "@/pages/hr/AttendanceSettingsPage";
import PayrollPage from "@/pages/hr/PayrollPage";
import PerformancePage from "@/pages/hr/PerformancePage";
import AssetsPage from "@/pages/hr/AssetsPage";
import AnalyticsPage from "@/pages/hr/AnalyticsPage";
import BioCloudSyncPage from "@/pages/hr/BioCloudSyncPage";
import CompanyDocumentsPage from "@/pages/hr/CompanyDocumentsPage";
import HRApprovalQueuePage from "@/pages/hr/HRApprovalQueuePage";
import WhosInPage from "@/pages/hr/WhosInPage";
import HRReportsPage from "@/pages/hr/HRReportsPage";
import EmployeeValueScorePage from "@/pages/hr/EmployeeValueScorePage";
import AttendanceGapPage from "@/pages/hr/AttendanceGapPage";
import RegularizationPage from "@/pages/hr/RegularizationPage";
import RegularizationApprovalsPage from "@/pages/hr/RegularizationApprovalsPage";
import LeaveBalancePage from "@/pages/hr/LeaveBalancePage";
import LeaveApprovalsPage from "@/pages/hr/LeaveApprovalsPage";
import TimesheetPage from "@/pages/hr/TimesheetPage";
import TimesheetTeamPage from "@/pages/hr/TimesheetTeamPage";
import WFHVerificationPage from "@/pages/hr/WFHVerificationPage";
import ApprovalPipelinePage from "@/pages/hr/ApprovalPipelinePage";
import PrePayrollVerificationPage from "@/pages/hr/PrePayrollVerificationPage";
import DepartmentPerformancePage from "@/pages/hr/DepartmentPerformancePage";
import SSHRTimesheetPage from "@/pages/sshr/SSHRTimesheetPage";
import ShiftMasterPage from "@/pages/hr/ShiftMasterPage";
import ShiftAssignmentPage from "@/pages/hr/ShiftAssignmentPage";
import LateRulesPage from "@/pages/hr/LateRulesPage";
import MentorRosterPage from "@/pages/hr/MentorRosterPage";

// SSHR (Self-Service HR) Module
import SSHRPage from "@/pages/SSHRPage";
import PayslipsPage from "@/pages/PayslipsPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import CalendarPage from "@/pages/CalendarPage";

// Marketing Module
import MarketingDashboardPage from "@/pages/marketing/MarketingDashboardPage";
import MarketingSettingsPage from "@/pages/marketing/MarketingSettingsPage";
import MarketingConnectorsPage from "@/pages/marketing/MarketingConnectorsPage";
import MarketAnalysisPage from "@/pages/marketing/MarketAnalysisPage";
import ContentStudioPage from "@/pages/marketing/ContentStudioPage";
import MarketingCalendarPage from "@/pages/marketing/MarketingCalendarPage";

// Approvals
import ApprovalsPage from "@/pages/ApprovalsPage";

// Operational Controls
import TransferRequestsPage from "@/pages/TransferRequestsPage";
import RoundRobinPage from "@/pages/RoundRobinPage";
import SLAManagementPage from "@/pages/SLAManagementPage";

import Layout from "@/components/Layout";

// Protected Route Component — enforces DB-driven access control
const ProtectedRoute = ({ children }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const { canAccess, loading: permLoading } = usePermissions();
    const location = useLocation();
    
    if (loading || permLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    // Super admin, COO, and CEO always have full access
    if (user?.role === 'super_admin' || user?.role === 'coo' || user?.role === 'ceo') {
        return children;
    }
    
    // Check permission-based access from the Access Control settings
    if (!canAccess(location.pathname)) {
        return <Navigate to="/home" replace />;
    }
    
    return children;
};

// Public Route (redirect to welcome if just logged in, or home if already authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, justLoggedIn } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        // If user just logged in, redirect to welcome animation
        if (justLoggedIn) {
            return <Navigate to="/welcome" replace />;
        }
        // Otherwise, redirect to home
        return <Navigate to="/home" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            
            {/* Welcome Animation - Shows after login */}
            <Route path="/welcome" element={
                <ProtectedRoute>
                    <WelcomePage />
                </ProtectedRoute>
            } />

            {/* QR scan landing — protected but uses minimal chrome (no sidebar) */}
            <Route path="/asset-scan/:id" element={
                <ProtectedRoute>
                    <AssetScanPage />
                </ProtectedRoute>
            } />
            
            {/* Protected Routes with Layout */}
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="home" element={<div />} />
                <Route path="dashboard" element={<BirdsEyeDashboard />} />
                <Route path="dashboard/detailed" element={<DashboardPage />} />
                
                {/* SSHR (Self-Service HR) - Available to all logged in users */}
                <Route path="sshr" element={<SSHRPage />} />
                <Route path="sshr/payslips" element={<PayslipsPage />} />
                <Route path="sshr/announcements" element={<AnnouncementsPage />} />
                <Route path="sshr/calendar" element={<CalendarPage />} />
                <Route path="sshr/timesheet" element={<SSHRTimesheetPage />} />
                
                {/* Today's Follow-ups */}
                <Route path="followups" element={<FollowupsPage />} />
                
                {/* Sales CRM */}
                <Route path="sales" element={<SalesCRMPage />} />
                <Route path="sales/dashboard" element={<SalesDashboard />} />
                <Route path="sales/dormant-leads" element={<DormantLeadsPage />} />
                <Route path="sales/returning-leads" element={<ReturningLeadsPage />} />
                <Route path="sales/assignment-audit" element={<LeadAssignmentAuditPage />} />
                <Route path="analytics/lead-analysis" element={<LeadAnalysisPage />} />
                <Route path="productivity" element={<ProductivityDashboardPage />} />
                
                {/* Sales Directory (CEO, COO, Sales Manager) */}
                <Route path="sales/directory" element={<SalesDirectoryPage />} />

                {/* Historical Sales Import (Super Admin only) */}
                <Route path="sales/historical-import" element={<HistoricalImportPage />} />
                
                {/* CS Historical Import (Super Admin only) */}
                <Route path="cs/historical-import" element={<CSHistoricalImportPage />} />
                
                {/* Leads Pool */}
                <Route path="leads/pool" element={<LeadsPoolPage />} />
                
                {/* Approval Requests */}
                <Route path="approvals" element={<ApprovalsPage />} />
                
                {/* Customer Master */}
                <Route path="customers" element={<CustomerMasterPage />} />
                
                {/* Customer Service */}
                <Route path="cs" element={<CustomerServicePage />} />
                <Route path="cs/dashboard" element={<CSDashboard />} />
                <Route path="cs/merge-approvals" element={<MergeApprovalsPage />} />
                <Route path="operations/duplicate-manager" element={<DuplicateManagerPage />} />
                <Route path="cs/directory" element={<StudentDirectoryPage />} />
                
                {/* Mentor CRM */}
                <Route path="mentor" element={<MentorCRMPage />} />
                <Route path="mentor/dashboard" element={<MentorDashboardPage />} />
                <Route path="mentor/leaderboard" element={<MentorLeaderboard />} />
                <Route path="mentor/cross-deposits" element={<CrossMentorDepositsPage />} />
                <Route path="mentor/historical-import" element={<MentorHistoricalImportPage />} />
                <Route path="mt5" element={<MT5SyncPage />} />
                
                {/* Business Development */}
                <Route path="bd" element={<BDCRMPage />} />
                <Route path="bd/dashboard" element={<BDDashboardPage />} />
                <Route path="bd/calculator" element={<BDCalculatorPage />} />
                <Route path="call-log" element={<SharedCallLogPage />} />
                <Route path="meta-hub" element={<MetaHubPage />} />
                
                {/* Certificates, Reports, Forecasting */}
                <Route path="certificates" element={<CertificatePage />} />
                <Route path="reports" element={<ReportBuilderPage />} />
                <Route path="reports/productivity" element={<ProductivityReportPage />} />
                <Route path="reports/scheduler" element={<ReportSchedulerPage />} />
                <Route path="forecasting" element={<RevenueForecastPage />} />
                <Route path="cs/student-portal" element={<StudentPortalPage />} />
                
                {/* Finance - Entity Selection */}
                <Route path="finance" element={<FinanceEntitySelector />} />
                
                {/* Finance - Entity Layout with Sub-routes */}
                <Route path="finance/:entity/*" element={<FinanceLayout />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    {/* Dashboard - uses router to select correct dashboard based on entity */}
                    <Route path="dashboard" element={<FinanceDashboardRouter />} />
                    {/* CLT Routes */}
                    <Route path="payables" element={<CltPayablesPage />} />
                    <Route path="receivables" element={<CltReceivablesPage />} />
                    <Route path="journal" element={<JournalPage />} />
                    <Route path="settlements" element={<SettlementsPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="transfers" element={<TransfersPage />} />
                    <Route path="accounts" element={<AccountsPage />} />
                    <Route path="commission-engine" element={<FinanceCommissionEnginePage />} />
                    <Route path="commission-settlements" element={<CommissionSettlementsPage />} />
                    <Route path="reconciliation" element={<ReconciliationPage />} />
                    {/* Treasury Routes */}
                    <Route path="balances" element={<TreasuryBalancesPage />} />
                    {/* Budgeting Routes */}
                    <Route path="sheet" element={<BudgetSheetPage />} />
                    {/* Data Management */}
                    <Route path="management" element={<DataManagementPage />} />
                    {/* Payment Verifications */}
                    <Route path="verifications" element={<FinanceVerificationsPage />} />
                    {/* Pending Settlements */}
                    <Route path="pending-settlements" element={<PendingSettlementsPage />} />
                    {/* Finance Settings Routes */}
                    <Route path="cost-centers" element={<FinanceSettingsPage section="cost-centers" />} />
                    <Route path="payment-methods" element={<FinanceSettingsPage section="payment-methods" />} />
                    <Route path="payment-gateways" element={<FinanceSettingsPage section="payment-gateways" />} />
                    <Route path="psp-mapping" element={<FinanceSettingsPage section="psp-mapping" />} />
                    <Route path="chart-of-accounts" element={<FinanceSettingsPage section="accounts" />} />
                    <Route path="bank-accounts" element={<FinanceSettingsPage section="bank-accounts" />} />
                    {/* Vendor Management */}
                    <Route path="vendors" element={<VendorManagementPage />} />
                    {/* Unified Transactions */}
                    <Route path="transactions" element={<UnifiedTransactionsPage />} />
                </Route>
                
                {/* Finance - Mentor Withdrawals (standalone) */}
                <Route path="finance/mentor-withdrawals" element={<WithdrawalsPage />} />
                
                {/* HR Module */}
                <Route path="hr/dashboard" element={<HRDashboard />} />
                <Route path="hr/employees" element={<EmployeeMasterPage />} />
                <Route path="hr/employees/:id" element={<EmployeeDetailsPage />} />
                <Route path="hr/leave" element={<LeavePage />} />
                <Route path="hr/attendance" element={<AttendancePage />} />
                <Route path="hr/attendance-settings" element={<AttendanceSettingsPage />} />
                <Route path="hr/biocloud" element={<BioCloudSyncPage />} />
                <Route path="hr/payroll" element={<PayrollPage />} />
                <Route path="hr/performance" element={<PerformancePage />} />
                <Route path="hr/assets" element={<AssetsPage />} />
                <Route path="hr/analytics" element={<AnalyticsPage />} />
                <Route path="hr/documents" element={<CompanyDocumentsPage />} />
                <Route path="hr/approvals" element={<HRApprovalQueuePage />} />
                <Route path="hr/whos-in" element={<WhosInPage />} />
                <Route path="hr/reports" element={<HRReportsPage />} />
                <Route path="hr/employee-value" element={<EmployeeValueScorePage />} />
                <Route path="hr/attendance-gap" element={<AttendanceGapPage />} />
                <Route path="hr/bulk-attendance" element={<BulkAttendancePage />} />
                <Route path="hr/regularization" element={<RegularizationPage />} />
                <Route path="hr/regularization-approvals" element={<RegularizationApprovalsPage />} />
                <Route path="hr/leave-balance" element={<LeaveBalancePage />} />
                <Route path="hr/leave-approvals" element={<LeaveApprovalsPage />} />
                <Route path="hr/timesheet" element={<TimesheetPage />} />
                <Route path="hr/timesheet-team" element={<TimesheetTeamPage />} />
                <Route path="hr/wfh-verification" element={<WFHVerificationPage />} />
                <Route path="hr/approval-pipeline" element={<ApprovalPipelinePage />} />
                <Route path="hr/payroll/pre-run" element={<PrePayrollVerificationPage />} />
                <Route path="hr/department-performance" element={<DepartmentPerformancePage />} />
                <Route path="hr/shift-master" element={<ShiftMasterPage />} />
                <Route path="hr/shift-assignment" element={<ShiftAssignmentPage />} />
                <Route path="hr/late-rules" element={<LateRulesPage />} />
                <Route path="hr/mentor-roster" element={<MentorRosterPage />} />
                
                {/* Marketing Module */}
                <Route path="marketing" element={<MarketingDashboardPage />} />
                <Route path="marketing/dashboard" element={<MarketingDashboardPage />} />
                <Route path="marketing/settings" element={<MarketingSettingsPage />} />
                <Route path="marketing/connectors" element={<MarketingConnectorsPage />} />
                <Route path="marketing/analysis" element={<MarketAnalysisPage />} />
                <Route path="marketing/content-studio" element={<ContentStudioPage />} />
                <Route path="marketing/calendar" element={<MarketingCalendarPage />} />
                
                {/* User Management */}
                <Route path="users" element={<UsersPage />} />
                
                {/* Department Management */}
                <Route path="departments" element={<DepartmentsPage />} />
                
                {/* Course Management */}
                <Route path="courses" element={<CoursesPage />} />
                
                {/* Commission Engine */}
                <Route path="commissions" element={<CommissionEnginePage />} />
                
                {/* Commission Dashboard */}
                <Route path="commission-dashboard" element={<CommissionDashboard />} />
                
                {/* Access Control */}
                <Route path="access-control" element={<AccessControlPage />} />
                
                {/* Role Management */}
                <Route path="roles" element={<RolesPage />} />
                
                {/* Teams Management */}
                <Route path="teams" element={<TeamsPage />} />
                
                {/* QC Dashboard */}
                <Route path="qc-dashboard" element={<QCDashboardPage />} />
                
                {/* Audit Log */}
                <Route path="audit-log" element={<AuditLogPage />} />
                
                {/* Password Reset Requests */}
                <Route path="password-resets" element={<PasswordResetPage />} />
                
                {/* People Intelligence (CEO/COO only) */}
                <Route path="people-intelligence" element={<SecurityIntelligencePage />} />
                
                {/* Competitor Intelligence (CEO/COO only) */}
                <Route path="competitor-intelligence" element={<CompetitorIntelligencePage />} />
                
                {/* Transfer Requests (Dual Approval) */}
                <Route path="transfer-requests" element={<TransferRequestsPage />} />
                
                {/* Round Robin Controls */}
                <Route path="round-robin" element={<RoundRobinPage />} />
                
                {/* SLA Management */}
                <Route path="sla-management" element={<SLAManagementPage />} />
                
                {/* Admin Settings - Data Reset & Feature Flags */}
                <Route path="admin-settings" element={<AdminSettingsPage />} />
                <Route path="admin-exports" element={<AdminExportsPage />} />
                <Route path="developer-guide" element={<DeveloperGuidePage />} />
                
                {/* Internal Chat */}
                <Route path="chat" element={<ChatPage />} />
                
                {/* Executive Dashboard */}
                <Route path="executive" element={<ExecutiveDashboard />} />
                
                {/* Organization Map */}
                {/* Organization Map */}
                <Route path="organization" element={<OrganizationMapPage />} />
                
                {/* IT Assets */}
                <Route path="it/assets" element={<ITAssetsPage />} />
                <Route path="it/asset-management" element={<AssetManagementPage />} />
                <Route path="it/saas" element={<SaaSSubscriptionsPage />} />
                <Route path="marketing/toolkit" element={<MarketingExtrasPage />} />
                <Route path="hr/offboarding" element={<OffboardingPage />} />
                
                {/* Task Manager */}
                <Route path="hr/tasks" element={<TaskManagerPage />} />
                
                {/* Settings */}
                <Route path="settings" element={<SettingsPage />} />
                
                {/* Knowledge Base */}
                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                
                {/* Claret Dashboard */}
                <Route path="claret" element={<ClaretDashboardPage />} />
                <Route path="claret/admin" element={<HiddenRoute><ClaretAdminPage /></HiddenRoute>} />
                <Route path="claret/config" element={<ClaretConfigPage />} />
                <Route path="claret/objections" element={<ObjectionLibraryPage />} />
                <Route path="chat-monitor" element={<HiddenRoute><ChatMonitorPage /></HiddenRoute>} />
                <Route path="call-intelligence" element={<CallIntelligencePage />} />
                <Route path="call-analytics" element={<CallAnalyticsPage />} />
                <Route path="script-learning" element={<ScriptLearningPage />} />
                <Route path="realtime-assist" element={<RealtimeAssistPage />} />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ErrorBoundary label="CLT ERP">
            <ThemeProvider>
                <AuthProvider>
                    <PermissionProvider>
                        <BrowserRouter>
                            <AppRoutes />
                            <Toaster position="top-right" richColors />
                        </BrowserRouter>
                    </PermissionProvider>
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
